import React, { useState, useEffect, useCallback } from 'react'; // eslint-disable-line no-unused-vars
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import instructorService from '../services/instructorService';
import './BecomeInstructor.css';

function BecomeInstructor() {
  const { user } = useUser();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    bio: '',
    expertise: [],
    experience: '',
    sampleCourse: '',
    socialLinks: {
      linkedin: '',
      twitter: '',
      website: ''
    },
    agreeToTerms: false
  });
  
  const [currentExpertise, setCurrentExpertise] = useState('');
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadApplication = useCallback(async () => {
    if (!user) return;
    
    const app = await instructorService.getApplicationStatus(user.uid);
    setApplication(app);
  }, [user]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
      
      // Check existing application
      loadApplication();
    }
  }, [user, loadApplication]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('social.')) {
      const socialKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        socialLinks: {
          ...prev.socialLinks,
          [socialKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const addExpertise = () => {
    if (currentExpertise.trim() && !formData.expertise.includes(currentExpertise.trim())) {
      setFormData(prev => ({
        ...prev,
        expertise: [...prev.expertise, currentExpertise.trim()]
      }));
      setCurrentExpertise('');
    }
  };

  const removeExpertise = (skill) => {
    setFormData(prev => ({
      ...prev,
      expertise: prev.expertise.filter(s => s !== skill)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!user) {
      setError('Please sign in to apply');
      return;
    }
    
    if (!formData.agreeToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }
    
    if (formData.expertise.length === 0) {
      setError('Please add at least one area of expertise');
      return;
    }
    
    setLoading(true);
    
    try {
      const applicationData = {
        userId: user.uid,
        ...formData
      };
      
      await instructorService.applyAsInstructor(applicationData);
      
      // Reload application status
      await loadApplication();
      
      alert('🎉 Application submitted successfully! We\'ll review it soon.');
    } catch (err) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // Show application status if exists
  if (application) {
    return (
      <div className="become-instructor">
        <div className="application-status-container">
          <div className="application-status">
            <h1>📋 Application Status</h1>
            
            <div className={`status-badge ${application.status}`}>
              {application.status === 'pending' && '⏳ Under Review'}
              {application.status === 'approved' && '✅ Approved!'}
              {application.status === 'rejected' && '❌ Not Approved'}
            </div>
            
            <div className="application-details">
              <p><strong>Submitted:</strong> {new Date(application.submittedAt?.toDate()).toLocaleDateString()}</p>
              
              {application.status === 'approved' && (
                <>
                  <p className="success-message">
                    🎉 Congratulations! You're now an approved instructor!
                  </p>
                  <button 
                    onClick={() => navigate('/instructor/dashboard')}
                    className="dashboard-btn"
                  >
                    Go to Instructor Dashboard →
                  </button>
                </>
              )}
              
              {application.status === 'pending' && (
                <p className="pending-message">
                  ⏳ Your application is being reviewed. We'll notify you soon!
                </p>
              )}
              
              {application.status === 'rejected' && application.reviewNotes && (
                <div className="review-notes">
                  <h3>Review Notes:</h3>
                  <p>{application.reviewNotes}</p>
                  <button 
                    onClick={() => setApplication(null)}
                    className="reapply-btn"
                  >
                    Apply Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="become-instructor">
      <div className="instructor-hero">
        <h1>🎓 Become an Instructor</h1>
        <p>Share your knowledge and earn money teaching students worldwide!</p>
      </div>

      <div className="benefits-section">
        <h2>Why Teach With Us?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <span className="benefit-icon">💰</span>
            <h3>Earn Money</h3>
            <p>Get 70% revenue share on all your courses</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-icon">🌍</span>
            <h3>Global Reach</h3>
            <p>Teach students from around the world</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-icon">📊</span>
            <h3>Analytics</h3>
            <p>Track your performance with detailed insights</p>
          </div>
          <div className="benefit-card">
            <span className="benefit-icon">🎯</span>
            <h3>Flexibility</h3>
            <p>Create content on your own schedule</p>
          </div>
        </div>
      </div>

      <div className="application-form-container">
        <h2>Apply to Become an Instructor</h2>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="application-form">
          <div className="form-section">
            <h3>Personal Information</h3>
            
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
              />
            </div>
            
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="john@example.com"
              />
            </div>
            
            <div className="form-group">
              <label>Bio *</label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder="Tell us about yourself and your teaching experience..."
              />
              <small>{formData.bio.length}/500 characters</small>
            </div>
          </div>

          <div className="form-section">
            <h3>Expertise & Experience</h3>
            
            <div className="form-group">
              <label>Areas of Expertise *</label>
              <div className="expertise-input">
                <input
                  type="text"
                  value={currentExpertise}
                  onChange={(e) => setCurrentExpertise(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExpertise())}
                  placeholder="e.g., Python, JavaScript, Web Development"
                />
                <button type="button" onClick={addExpertise} className="add-btn">
                  Add
                </button>
              </div>
              
              {formData.expertise.length > 0 && (
                <div className="expertise-tags">
                  {formData.expertise.map((skill, index) => (
                    <span key={index} className="expertise-tag">
                      {skill}
                      <button 
                        type="button" 
                        onClick={() => removeExpertise(skill)}
                        className="remove-tag"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Teaching Experience *</label>
              <textarea
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                required
                rows={4}
                placeholder="Describe your teaching experience, certifications, or relevant background..."
              />
            </div>
            
            <div className="form-group">
              <label>Sample Course Idea *</label>
              <textarea
                name="sampleCourse"
                value={formData.sampleCourse}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="What course would you like to create? Describe the topic and target audience..."
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Social Links (Optional)</h3>
            
            <div className="form-group">
              <label>LinkedIn</label>
              <input
                type="url"
                name="social.linkedin"
                value={formData.socialLinks.linkedin}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>
            
            <div className="form-group">
              <label>Twitter</label>
              <input
                type="url"
                name="social.twitter"
                value={formData.socialLinks.twitter}
                onChange={handleInputChange}
                placeholder="https://twitter.com/yourusername"
              />
            </div>
            
            <div className="form-group">
              <label>Website</label>
              <input
                type="url"
                name="social.website"
                value={formData.socialLinks.website}
                onChange={handleInputChange}
                placeholder="https://yourwebsite.com"
              />
            </div>
          </div>

          <div className="form-section">
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                />
                <span>
                  I agree to the <a href="/terms-of-service" target="_blank">Terms of Service</a> and 
                  <a href="/instructor-agreement" target="_blank">Instructor Agreement</a> *
                </span>
              </label>
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? '⏳ Submitting...' : '🚀 Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default BecomeInstructor;
